<script setup lang="ts">
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useClient } from '../../utils/client-only'
import { useAdaStore } from '../../stores/ada'
import AdaAdSlot from './AdaAdSlot.vue'
import AdaNativeCrossbar from './AdaNativeCrossbar.vue'

import type { AdSlotName } from './AdaAdSlot.vue'
import type { AdSlotMarkerPayload } from '../../stores/ada'
import type { ScreenType } from '../../composables/use-ada-dynamic-slot-loader'
import type { BxMbWindow } from '../../types'

// define paragraphs where we don't want to show ad slots afterwards
const EXCLUDE_PARAGRAPHS = ['video', 'interline', 'horizontalLine', 'adaNativeCrossbar', 'buttonGroup', 'lexicon']

type AboveParagraph = {
  index?: number | string,
  type?: string | null,
  wordcount?: number,
  nested?: boolean,
  hasAsyncContent?: boolean
}

type ActivatedAdSlotMarkerPayload = AdSlotMarkerPayload & {
  innerWidth: number,
  parentOffsetTop: number,
  renderSlot?: string | false,
  screenType?: ScreenType
}

const props = withDefaults(defineProps<{
  aboveParagraph?: AboveParagraph
}>(), {
  aboveParagraph: () => ({
    index: -1,
    type: null,
    wordcount: -1,
    nested: false
  })
})

const emit = defineEmits<{ remove: [] }>()

const { isClient } = useClient()
const adaStore = useAdaStore()
const { getActivatedAdSlotMarker, isAsyncContentMode, asyncContentRefresh } = storeToRefs(adaStore)
const { addAdSlotMarker } = adaStore

const markerEl = ref<HTMLElement | null>(null)
const offsetTop = ref(-1)
const enabled = ref(true)
const renderSlot = ref<string | false>(false)
const renderNativeCrossbarTop = ref(false)
const parentOffsetTop = ref<number | null>(null)
const innerWidth = ref<number | null>(null)
const parentInnerHeight = ref<number | null>(null)

const adaSlotData = computed(() => {
  if (!renderSlot.value) return undefined
  let [slotName, slotId] = renderSlot.value.split('_')
  if (slotName === 'outstream') {
    slotName = 'outstream_div'
    slotId = '0'
  }
  return {
    slotName: slotName as AdSlotName,
    slotId: parseInt(slotId)
  }
})

/**
 * disable and remove ad slot marker
 */
const removeAdSlotMarker = () => {
  enabled.value = renderNativeCrossbarTop.value = false
  renderSlot.value = false
  /**
   * while we are in async content mode, we will not remove the marker,
   * but just disable it, so it won't be rendered.
   * we need all disabled markers, once the content changes,
   * to recalculate the distribution of the slots.
   * exception are the markers within async content, which we always remove,
   * because on content change the slots will be redistributed.
   * if we are not in async content mode, we will remove the marker
   * so it won't be rendered anymore.
   * if we have async content and the refresh is in progress, we will just enable the
   * marker again, so it can be rendered once the content is ready.
   */
  if (props.aboveParagraph.hasAsyncContent || !isAsyncContentMode.value) {
    emit('remove')
  } else if (asyncContentRefresh.value) {
    enabled.value = true
    nextTick(() => {
      registerAdSlotMarker()
    })
  }
}

const registerAdSlotMarker = () => {
  // fetch marker position and width
  enabled.value = true
  const marker = markerEl.value
  if (marker) {
    offsetTop.value = Math.floor(marker.getBoundingClientRect().top + window.scrollY)
    // we need a new innerWidth check for superbanner placement cause content width could be to low
    innerWidth.value = marker.getBoundingClientRect().width
    parentOffsetTop.value = Math.floor(marker.parentElement!.getBoundingClientRect().top + window.scrollY)
    parentInnerHeight.value = marker.parentElement!.getBoundingClientRect().height
    // push marker data to store and watch for activation request of a specific ad slot
    addAdSlotMarker({
      index: `${props.aboveParagraph.index}`,
      type: `${props.aboveParagraph.type}`,
      offsetTop: offsetTop.value,
      innerWidth: innerWidth.value,
      parentOffsetTop: parentOffsetTop.value,
      parentInnerHeight: parentInnerHeight.value
    })
  } else {
    removeAdSlotMarker()
  }
}

/**
 * called by watcher if we have an activation request for a specific ad slot
 */
const activateRequestedSlot = (activationData: ActivatedAdSlotMarkerPayload) => {
  const MIN_HEIGHT_FOR_RECTANGLE_ASIDE_PLACEMENT = 125
  if (activationData.renderSlot === 'ncb_top') {
    renderNativeCrossbarTop.value = true
    return
  }
  const parentNode = markerEl.value?.parentElement
  const slotToRender = activationData.renderSlot as string
  const bxWindow = window as unknown as BxMbWindow
  renderSlot.value = slotToRender
  if (
    (
      slotToRender.includes('rectangle') &&
      activationData.screenType === 'largeDesktop' &&
      !props.aboveParagraph?.nested &&
      (parentInnerHeight.value ?? 0) >= MIN_HEIGHT_FOR_RECTANGLE_ASIDE_PLACEMENT &&
      !bxWindow.bxMb.advertorial &&
      !bxWindow.bxMb.adZone
    ) ||
    slotToRender.includes('ncb_top')
  ) {
    // shrink the parent container to place the rectangle ad slot aside
    parentNode?.classList.add('bx-collection', 'bx-ada--slot-wrapper')
  }
  // If the next sibling has a highlight-followup style, we replace it with highlight-start.
  // This ensures that a margin is displayed at the top.
  // THIS applies in any case, regardless of whether we previously placed it next to or below it.
  if (!parentNode?.nextElementSibling?.classList.contains('bx-teaser-container--highlight-first')) {
    parentNode?.nextElementSibling?.classList.replace('bx-teaser-container--highlight-followup', 'bx-teaser-container--highlight-first')
  }
}

/**
 * Watch for activation request of a specific ad slot or disable the ad slot marker
 */
watch(getActivatedAdSlotMarker, (newVal) => {
  const activationData = newVal[offsetTop.value] as ActivatedAdSlotMarkerPayload | undefined
  if (activationData?.renderSlot) {
    activateRequestedSlot(activationData)
  } else {
    removeAdSlotMarker()
  }
})

onBeforeMount(() => {
  // directly remove ad slot marker if we want to prevent ad slots after specific paragraph types
  if (EXCLUDE_PARAGRAPHS.includes(props.aboveParagraph.type ?? '')) {
    removeAdSlotMarker()
  } else {
    // lets wait for the next tick to get the correct offsetTop
    nextTick(() => {
      registerAdSlotMarker()
    })
  }
})
</script>

<template>
  <div v-if="isClient && enabled"
       ref="markerEl"
       class="bx-ada-marker">
    <AdaAdSlot v-if="adaSlotData"
               :slot-name="adaSlotData.slotName"
               :slot-id="adaSlotData.slotId" />
    <AdaNativeCrossbar v-if="renderNativeCrossbarTop"
                       position="top" />
  </div>
</template>
